import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { trans } from '@spotahome/soyuz/client';
import classNames from 'classnames';

import { Button, CloseButton } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import MagnifyingGlassIcon from '@spotahome/ui-library/src/tenant/components/icons/MagnifyingGlassIcon';

import mapPointShape from '../../shapes/mapPoint';

import useAutocompleteService from './useAutocompleteService';
import PlacesAutocompleteSuggestion from './PlacesAutocompleteSuggestion';

import styles from './PlacesAutocompleteRedesign.module.scss';

const BASE_CLASS = 'places-autocomplete-redesign';

const noop = () => {};

const PlacesAutocompleteRedesign = ({
  pointOfInterest = undefined,
  boundCoords,
  countryCode = undefined,
  cityName,
  onPlaceSelected = noop,
  renderExtraContent = noop,
  className = '',
  placeholder = ''
}) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const { handleInput, inputValue, suggestionList, placesRef, handleSelect } =
    useAutocompleteService({
      boundCoords,
      countryCode,
      pointOfInterest,
      onPlaceSelected
    });

  const handleSearchClick = e => {
    e.preventDefault();
    inputRef.current.focus();
    inputRef.current.value = '';
  };

  const placeholderText = useMemo(() => {
    return isFocused
      ? ''
      : placeholder ||
          trans('rentable_unit_map.autocomplete.placeholder', {
            city: cityName
          });
  }, [isFocused, placeholder, cityName]);

  return (
    <div
      className={classNames(styles[BASE_CLASS], className)}
      ref={placesRef}
      data-test="map-search-bar-redesign"
    >
      <DSIcon name="home_work" className={styles[`${BASE_CLASS}__icon`]} />
      <div className={styles[`${BASE_CLASS}__box`]}>
        <input
          ref={inputRef}
          name="places-autocomplete-input"
          className={styles[`${BASE_CLASS}__input`]}
          type="text"
          value={inputValue || ''}
          onChange={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholderText}
          autoComplete="off"
        />
        {pointOfInterest && (
          <CloseButton
            onClick={() => onPlaceSelected(null)}
            className={styles[`${BASE_CLASS}__clear`]}
            role="button"
            tabIndex={0}
            data-test="map-search-clear"
          />
        )}
      </div>
      {suggestionList.length ? (
        <ul
          className={styles[`${BASE_CLASS}__suggestions`]}
          data-test="poi-search-suggestions"
        >
          {suggestionList.map(suggestion => (
            <PlacesAutocompleteSuggestion
              key={suggestion.place_id}
              structuredFormatting={suggestion.structured_formatting}
              onClick={handleSelect(
                suggestion.place_id,
                suggestion.description
              )}
            />
          ))}
        </ul>
      ) : null}
      <Button
        type="button"
        fullWidth
        size="big"
        className={styles[`${BASE_CLASS}__button`]}
        data-test="places-search-button"
        onClick={handleSearchClick}
      >
        <MagnifyingGlassIcon width={16} height={16} />
      </Button>
      {renderExtraContent()}
    </div>
  );
};

PlacesAutocompleteRedesign.propTypes = {
  boundCoords: PropTypes.shape(mapPointShape).isRequired,
  pointOfInterest: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    name: PropTypes.string,
    placeId: PropTypes.string
  }),
  countryCode: PropTypes.string,
  cityName: PropTypes.string.isRequired,
  onPlaceSelected: PropTypes.func,
  renderExtraContent: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string
};

export default PlacesAutocompleteRedesign;
